import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiPlus, FiTrash2, FiCheck, FiChevronDown, FiAlertCircle } = FiIcons;
import { getQuestionsForDimension, LIKERT_SCALE } from '../../data/nordicModelData';

const QuestionSelector = ({ dimension, onUpdate, initialSelected = [] }) => {
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedIds, setSelectedIds] = useState(initialSelected);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setAvailableQuestions(getQuestionsForDimension(dimension.id));
  }, [dimension.id]);

  const toggleQuestion = (id) => {
    const newSelection = selectedIds.includes(id)
      ? selectedIds.filter(qId => qId !== id)
      : [...selectedIds, id];
    
    if (newSelection.length <= 50) {
      setSelectedIds(newSelection);
      onUpdate(dimension.id, newSelection);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all">
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl bg-${dimension.color}-50 text-${dimension.color}-600 flex items-center justify-center`}>
            <SafeIcon icon={FiIcons[dimension.icon]} size={20} />
          </div>
          <div>
            <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight">{dimension.label}</h4>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {selectedIds.length} / 50 spørsmål valgt
              </span>
              {selectedIds.length === 0 && (
                <span className="flex items-center gap-1 text-[10px] font-bold text-rose-500 uppercase tracking-widest">
                  <SafeIcon icon={FiAlertCircle} size={10} /> Påkrevd
                </span>
              )}
            </div>
          </div>
        </div>
        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }}>
          <SafeIcon icon={FiChevronDown} className="text-slate-300" />
        </motion.div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="border-t border-slate-50"
          >
            <div className="p-6 space-y-3">
              {availableQuestions.map((q) => {
                const isSelected = selectedIds.includes(q.id);
                return (
                  <div 
                    key={q.id}
                    onClick={() => toggleQuestion(q.id)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-start gap-4 ${
                      isSelected 
                      ? `border-${dimension.color}-200 bg-${dimension.color}-50/30` 
                      : 'border-slate-100 hover:border-slate-200'
                    }`}
                  >
                    <div className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected ? `bg-${dimension.color}-600 border-${dimension.color}-600 text-white` : 'border-slate-200'
                    }`}>
                      {isSelected && <SafeIcon icon={FiCheck} size={12} />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-700 leading-relaxed">{q.text}</p>
                      <div className="flex gap-4 mt-3">
                        {LIKERT_SCALE.map(s => (
                          <div key={s.value} className="flex flex-col items-center gap-1 opacity-40">
                            <div className="w-6 h-1 bg-slate-200 rounded-full" />
                            <span className="text-[8px] font-bold text-slate-400 uppercase">{s.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <button className="w-full p-4 border-2 border-dashed border-slate-100 rounded-2xl text-[10px] font-black text-slate-400 uppercase tracking-widest hover:border-slate-200 hover:text-slate-600 transition-all flex items-center justify-center gap-2">
                <SafeIcon icon={FiPlus} /> Legg til eget spørsmål
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuestionSelector;